import { defineStore } from 'pinia'
import { wait } from '@develop/ia-utils'
import { isClient } from '@vueuse/core'
import type { ModalName } from '@/models/modal'

/**
 * @example const modals = useModalManager()
 */
export const useModalManager = defineStore('modal-manager', () => {
  /**
   * ModalBase hide transition delay
   */
  const MODAL_DELAY = 150

  // Modals are still mounted until hidden animation is finished
  const mountedModals = ref<Set<ModalName>>(new Set())
  const openedModals = ref<Set<ModalName>>(new Set())

  const hasOpenedModals = computed(() => openedModals.value.size > 0)

  const activeModal = computed(() => Array.from(openedModals.value).at(-1))

  const isActive = (modal: ModalName) => activeModal.value === modal
  const isMounted = (modal: ModalName) => mountedModals.value.has(modal)
  const isIdle = (modal: ModalName) => !isActive(modal) && openedModals.value.has(modal)

  const ga = isClient ? useApofinderTracking() : undefined

  const show = (modal: ModalName) => {
    setTimeout(() => {
      openedModals.value.add(modal)
      mountedModals.value.add(modal)
      // TODO: extract this out and await show instead
      if (modal === 'ApofinderFilters')
        ga?.filterToggleEvent()
    }, MODAL_DELAY)
  }

  const hide = (modal: ModalName) => new Promise<boolean>((resolve) => {
    if (modal) {
      if (isIdle(modal)) {
        openedModals.value.delete(modal)
        mountedModals.value.delete(modal)
        resolve(true)
      } else {
        openedModals.value.delete(modal)

        wait(MODAL_DELAY).then(() => {
          mountedModals.value.delete(modal)
          resolve(true)
        })
      }
      if (modal === 'ApofinderFilters')
        ga?.filterToggleEvent()
    }
  })

  const hideAll = () => {
    // Hide from bottom up, so only the current active modal would be awaited
    // while others below would instantly be unmounted
    Array.from(openedModals.value).forEach(hide)
  }

  const chatModalVisible = ref(false)

  return {
    hasOpenedModals,
    isActive,
    isMounted,
    isIdle,
    show,
    hide,
    hideAll,
    chatModalVisible,
  }
})
